import React from "react";
import styled, { css } from "styled-components";
import { Avatar } from "@sonder/ui/source/styled/components/Avatar";
import { borders, colors } from "@sonder/ui/source/styled/theme";
import {
  SearchForTasksItem,
  SearchForTasksItemResourceType,
} from "core/queries/SearchForTasks";
import { CompletionStatus } from "search/components/CompletionStatus";
import { SearchCardCreatedAt } from "search/components/SearchCardCreatedAt";
import { SearchCardDueAt } from "search/components/SearchCardDueAt";
import { PriorityBadge } from "tasks/components/PriorityBadge";
import { parseGraphQLId } from "tasks/helpers";
import { FilterStatus } from "typings/globalGQLTypes";

const Wrapper = styled.div`
  background: ${colors.white};
  border: ${borders.standard};
  box-sizing: border-box;
  color: ${colors.gray1};
  cursor: pointer;
  display: flex;
  line-height: 1.5;
  margin-bottom: 0.714rem;
  padding: 1.714rem 2.286rem;

  &:hover {
    cursor: pointer;
    border: 1px solid ${colors.grayA};
  }

  em {
    background-color: ${colors.grayE};
    border-radius: 0.286rem;
    font-style: normal;
  }
`;

const StyledAvatar = styled(Avatar)`
  margin-right: 1.714rem;
`;

const sharedMarginRight = css`
  margin-right: 0.714rem;
`;

const StyledPriorityBadge = styled(PriorityBadge)`
  ${sharedMarginRight};
`;

const StyledCompletionStatus = styled(CompletionStatus)`
  ${sharedMarginRight};
`;

const Title = styled.div`
  ${sharedMarginRight};
  margin-bottom: 0.286rem;
`;

const SubTitle = styled.div`
  color: ${colors.gray1};
  font-size: 1.143rem;
  font-weight: bold;
  margin-bottom: 0.286rem;
`;

const Description = styled.div`
  color: ${colors.gray6};
  font-size: 1.143rem;
`;

const BodyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 75%;
`;

const BottomRowWrapper = styled.div`
  align-items: center;
  display: flex;
  margin-top: 0.857rem;
`;

const StyledSearchCardDueAt = styled(SearchCardDueAt)`
  margin-left: auto;
  white-space: nowrap;
`;

interface SearchCardProps {
  item: SearchForTasksItem;
}

export const SharedSearchCardContent = ({ item }: SearchCardProps) => {
  const { title, subtitle, description } = item;
  return (
    <>
      <Title dangerouslySetInnerHTML={{ __html: title }} />
      <SubTitle dangerouslySetInnerHTML={{ __html: subtitle }} />
      <Description dangerouslySetInnerHTML={{ __html: description }} />
    </>
  );
};

export const SearchCard = ({ item }: SearchCardProps) => {
  const { resource } = item;
  switch (resource.__typename) {
    case SearchForTasksItemResourceType.Task: {
      const { assignee, priority, status, createdAt, dueAt, id } = resource;
      return (
        <Wrapper
          onClick={() => window.open(`/task/${parseGraphQLId(id)}`, "_blank")}
        >
          <StyledAvatar
            imageUrl={assignee?.avatar?.url ?? null}
            name={assignee?.name ?? ""}
          />

          <BodyWrapper>
            <SharedSearchCardContent item={item} />
            <BottomRowWrapper>
              <StyledPriorityBadge priority={priority} />
              <StyledCompletionStatus status={status as FilterStatus} />
              <SearchCardCreatedAt createdAt={createdAt} />
            </BottomRowWrapper>
          </BodyWrapper>

          <StyledSearchCardDueAt dueAt={dueAt} />
        </Wrapper>
      );
    }
    default:
      return (
        <Wrapper>
          <BodyWrapper>
            <SharedSearchCardContent item={item} />
          </BodyWrapper>
        </Wrapper>
      );
  }
};
